import { useEffect, useId, useRef, useState, type PointerEvent } from 'react'

// A deliberate short drag switches endpoints; reversing requires another full threshold.
const SNAP_DISTANCE = 32

/** Preview only fixed widths while dragging; save the chosen endpoint on release. */
export function SidebarResizeHandle({
  collapsed,
  onChange,
  onResize,
}: {
  collapsed: boolean
  onChange: (collapsed: boolean) => void
  onResize: (preview: boolean | null) => void
}) {
  const handle = useRef<HTMLDivElement>(null)
  const drag = useRef<{ pointer: number; x: number; collapsed: boolean } | null>(null)
  const [preview, setPreview] = useState<boolean | null>(null)
  const helpId = useId()
  function finish(commit: boolean) {
    const current = drag.current
    if (!current) return
    drag.current = null
    if (commit) onChange(current.collapsed)
    onResize(null)
    setPreview(null)
    if (handle.current?.hasPointerCapture(current.pointer))
      handle.current.releasePointerCapture(current.pointer)
    handle.current?.blur()
  }
  useEffect(() => {
    const cancel = () => finish(false)
    window.addEventListener('resize', cancel)
    window.addEventListener('blur', cancel)
    return () => {
      window.removeEventListener('resize', cancel)
      window.removeEventListener('blur', cancel)
    }
  }, [onResize])
  function begin(event: PointerEvent<HTMLDivElement>) {
    if (event.button !== 0 || !event.isPrimary) return
    event.preventDefault()
    event.currentTarget.focus()
    event.currentTarget.setPointerCapture(event.pointerId)
    drag.current = { pointer: event.pointerId, x: event.clientX, collapsed }
    setPreview(collapsed)
    onResize(collapsed)
  }
  function move(event: PointerEvent<HTMLDivElement>) {
    const current = drag.current
    if (!current || current.pointer !== event.pointerId) return
    const distance = event.clientX - current.x
    if (current.collapsed ? distance < SNAP_DISTANCE : distance > -SNAP_DISTANCE) return
    current.collapsed = !current.collapsed
    current.x = event.clientX
    setPreview(current.collapsed)
    onResize(current.collapsed)
  }
  return (
    <>
      <div
        ref={handle}
        className="sidebar-resize-handle"
        role="separator"
        tabIndex={0}
        aria-label="Resize sidebar"
        aria-orientation="vertical"
        aria-controls="app-sidebar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={(preview ?? collapsed) ? 0 : 100}
        aria-valuetext={(preview ?? collapsed) ? 'Collapsed' : 'Expanded'}
        aria-describedby={helpId}
        title="Drag to resize sidebar"
        onPointerDown={begin}
        onPointerMove={move}
        onPointerUp={() => finish(true)}
        onPointerCancel={() => finish(false)}
        onLostPointerCapture={() => finish(false)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault()
            finish(false)
            return
          }
          const close = ['ArrowLeft', 'Home'].includes(event.key)
          const open = ['ArrowRight', 'End'].includes(event.key)
          if (close || open || event.key === 'Enter') {
            event.preventDefault()
            finish(false)
            onChange(close ? true : open ? false : !collapsed)
          }
        }}
      />
      <span id={helpId} className="visually-hidden">
        Drag the edge to collapse or expand, or use the left and right arrow keys.
      </span>
    </>
  )
}
